import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";
import { kiesSeizoen, useSeizoenen } from "@/features/session/useSeizoenen";
import { Reistijden } from "@/features/reistijden/Reistijden";
import { keepScrollPosition } from "@jbnl/shared-ui";

export function ReistijdenKorpsenPage() {
  const { t } = useTranslation([
    "paginas",
    "navigatie",
    "common",
    "reistijden",
  ]);
  const { data: me, isPending } = useMe();
  const { data: seizoenen } = useSeizoenen();
  const {
    seizoen: seizoenId,
    dag: dagId,
    pagina,
  } = useSearch({ from: "/reistijden/korpsen" });
  const navigate = useNavigate({ from: "/reistijden/korpsen" });

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <Card>
          <CardHeader>
            <CardTitle>{t("common:doors.noAccess")}</CardTitle>
            <CardDescription>
              {t("common:doors.noAccessDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  // A seizoen named in the URL wins; otherwise the one last picked on the Seizoenen screen;
  // otherwise the running one. An id that no longer exists is a page with nothing chosen, so it
  // falls back the same way rather than answering not-found.
  const seizoen = kiesSeizoen(seizoenen, seizoenId);

  return (
    <Shell name={me.name} wide>
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">
          {t("navigatie:paginas.reistijdenKorpsen.label")}
        </h1>
        {seizoen ? (
          <p className="text-sm text-muted-foreground">
            {t("common:seizoen.lopend", { naam: seizoen.name })}{" "}
            {t("reistijdenKorpsen.uitleg")}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("common:seizoen.geenBefore")}
            <Link className="jb-link" to="/seizoenen">
              {t("common:seizoen.link")}
            </Link>
            {t("common:seizoen.na")}
          </p>
        )}
      </header>

      {/* #172 asked for the opgaven here. They live on the wedstrijddag, which is the screen
          that holds the dag; this one exists for a reistijd that reverts silently (ADR-0013). */}
      {seizoen && dagId && (
        <p className="mb-4 text-sm">
          <Link
            className="jb-link"
            to="/wedstrijddagen"
            search={{ seizoen: seizoen.id, dag: dagId, tab: "ploegen" }}
          >
            {t("reistijden:dagen.naarDeelname")}
          </Link>
        </p>
      )}

      {seizoen && (
        <Reistijden
          seizoen={seizoen}
          dagId={dagId}
          // `pagina` resets on a new dag; `bron` survives it, like the wedstrijddag screen's
          // own filters.
          onKies={(id) =>
            void navigate({
              ...keepScrollPosition,
              search: (previous) => ({
                ...previous,
                seizoen: seizoen.id,
                dag: id,
                pagina: undefined,
              }),
            })
          }
          pagina={pagina}
          onPaginaChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, pagina: next }),
              ...keepScrollPosition,
            })
          }
        />
      )}
    </Shell>
  );
}
